import { IdentityCoordinator } from './coordinator';
import { IdentityRepository } from './repository';
import type { IdentitySyncPage } from './types';

const PROFILE_SYNC_PAGE_LIMIT = 200;
const MAX_PROFILE_SYNC_PAGES = 100_000;

export type IdentitySyncPageLoader = (request: {
  afterChangeCursor: number;
  throughChangeCursor: number;
  limit: number;
}) => Promise<IdentitySyncPage>;

export type IdentityReconnectResult =
  | { status: 'complete'; throughChangeCursor: number; pageCount: number }
  | { status: 'full_snapshot_required'; throughChangeCursor: number; pageCount: number };

/**
 * Applies one stateless, frozen IM reconnect interval to the Web identity cache.
 *
 * The previous page cursor is persisted only in the same local transaction as
 * that page's canonical HTTP identities. An expired cursor returns a recovery
 * signal without persisting the server high-water mark; the future normalized
 * relationship rebuild must complete before that cursor can be committed.
 */
export async function reconcileIdentityChanges(options: {
  ownerUserId: number;
  repository: IdentityRepository;
  coordinator: IdentityCoordinator;
  loadPage: IdentitySyncPageLoader;
  baseUrl?: string;
}): Promise<IdentityReconnectResult> {
  let afterChangeCursor = await options.repository.getLastAppliedCursor(options.ownerUserId);
  if (!Number.isSafeInteger(afterChangeCursor) || afterChangeCursor < 0) {
    throw new Error('Persisted identity reconnect cursor is invalid');
  }
  let throughChangeCursor = 0;

  for (let pageCount = 1; pageCount <= MAX_PROFILE_SYNC_PAGES; pageCount += 1) {
    const page = await options.loadPage({
      afterChangeCursor,
      throughChangeCursor,
      limit: PROFILE_SYNC_PAGE_LIMIT,
    });
    validatePage(page, { afterChangeCursor, throughChangeCursor });

    // The first response freezes the run. Every continuation echoes the exact
    // same bound, making the server read stateless and portable across nodes.
    throughChangeCursor = page.throughChangeCursor;
    if (page.fullSnapshotRequired) {
      return {
        status: 'full_snapshot_required',
        throughChangeCursor,
        pageCount,
      };
    }

    if (page.changedUsers.length > 0) {
      await options.coordinator.fetchAndCommit(
        page.changedUsers,
        options.baseUrl,
        page.nextChangeCursor,
      );
    } else {
      await options.coordinator.commitReconnectCursor(page.nextChangeCursor);
    }

    if (!page.hasMore) {
      return { status: 'complete', throughChangeCursor, pageCount };
    }
    afterChangeCursor = page.nextChangeCursor;
  }

  // This is a client-side circuit breaker against a malformed server that
  // returns an endless advancing chain. The already committed pages remain
  // valid, and the next reconnect resumes from the last durable cursor.
  throw new Error('Identity reconnect exceeded the maximum page count');
}

function validatePage(
  page: IdentitySyncPage,
  request: { afterChangeCursor: number; throughChangeCursor: number },
): void {
  const cursors = [page.nextChangeCursor, page.throughChangeCursor];
  if (cursors.some((cursor) => !Number.isSafeInteger(cursor) || cursor < 0)) {
    throw new Error('Identity reconnect response contains an invalid cursor');
  }
  if (page.throughChangeCursor < request.afterChangeCursor ||
      (request.throughChangeCursor !== 0 &&
       page.throughChangeCursor !== request.throughChangeCursor) ||
      page.nextChangeCursor < request.afterChangeCursor ||
      page.nextChangeCursor > page.throughChangeCursor) {
    throw new Error('Identity reconnect response violates the frozen interval');
  }
  if (page.changedUsers.length > PROFILE_SYNC_PAGE_LIMIT) {
    throw new Error('Identity reconnect response exceeds the page limit');
  }

  const seen = new Set<number>();
  for (const changed of page.changedUsers) {
    if (!Number.isSafeInteger(changed.userId) || changed.userId <= 0 ||
        !Number.isSafeInteger(changed.minProfileVersion) ||
        changed.minProfileVersion <= 0 || seen.has(changed.userId)) {
      throw new Error('Identity reconnect response contains an invalid user reference');
    }
    seen.add(changed.userId);
  }

  if (page.fullSnapshotRequired) {
    if (page.hasMore || page.changedUsers.length !== 0 ||
        page.nextChangeCursor !== page.throughChangeCursor) {
      throw new Error('Identity full-snapshot response contains an invalid page payload');
    }
    return;
  }
  if (page.hasMore) {
    if (page.changedUsers.length === 0 ||
        page.nextChangeCursor <= request.afterChangeCursor ||
        page.nextChangeCursor >= page.throughChangeCursor) {
      throw new Error('Identity reconnect continuation does not make progress');
    }
  } else if (page.nextChangeCursor !== page.throughChangeCursor) {
    throw new Error('Identity reconnect terminal page does not reach its frozen bound');
  }
}
